import React, { useState, useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import logo from "../reactnest.png";
import Contact from "./ContactPage";
import { Link } from "react-router-dom";

gsap.registerPlugin(ScrollTrigger);

// Logo component
const LogoPlaceholder = ({ className }) => (
  <div className={className}>
      <img src={logo} alt="React Nest Logo" className="w-14 h-14"/>    
  </div>
);

const Home = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  
  // Refs for GSAP animations
  const heroTitleRef = useRef(null);
  const heroSubtitleRef = useRef(null);
  const heroDescriptionRef = useRef(null);
  const heroButtonRef = useRef(null);
  const heroPreviewRef = useRef(null);
  const featuresSectionRef = useRef(null);
  const featureCardsRef = useRef([]);
  const downloadSectionRef = useRef(null);
  const downloadStatsRef = useRef([]);
  const aboutSectionRef = useRef(null);
  const aboutContentRef = useRef(null);
  const aboutTerminalRef = useRef(null);
  const floatingElementsRef = useRef([]);

  useEffect(() => {
     const ctx = gsap.context(() => {
    // Hero Section Animations with enhanced effects
    const heroTl = gsap.timeline({ defaults: { ease: 'power3.out' } });
    
    heroTl
      .from(heroTitleRef.current, {
        x: -200,
        opacity: 0,
        duration: 1,
        delay: 0.2,
        ease: 'power3.out'
      })
      .from(heroSubtitleRef.current, {
        scale: 0.5,
        opacity: 0,
        duration: 1,
        ease: 'back.out(1.7)'
      }, '-=0.5')
      .from(heroDescriptionRef.current, {
        y: 50,
        opacity: 0,
        duration: 0.8
      }, '-=0.4')
      .from(heroButtonRef.current, {
        scale: 0,
        opacity: 0,
        duration: 0.8,
        ease: 'elastic.out(1, 0.5)'
      }, '-=0.3')
      .from(heroPreviewRef.current, {
        x: 100,
        opacity: 0,
        duration: 1,
        scale: 0.8
      }, '-=1');

    // Continuous floating animation for hero title
    gsap.to(heroTitleRef.current, {
      y: -10,
      duration: 2,
      repeat: -1,
      yoyo: true,
      ease: 'sine.inOut'
    });

    // Continuous rotation animation for subtitle
    gsap.to(heroSubtitleRef.current, {
      backgroundPosition: '200% center',
      duration: 3,
      repeat: -1,
      ease: 'linear'
    });

    // Floating elements animation
    floatingElementsRef.current.forEach((el, index) => {
      if (el) {
        gsap.to(el, {
          y: -30,
          x: index % 2 === 0 ? 20 : -20,
          duration: 2 + index * 0.5,
          repeat: -1,
          yoyo: true,
          ease: 'sine.inOut',
          delay: index * 0.2
        });

        gsap.to(el, {
          rotation: index % 2 === 0 ? 360 : -360,
          duration: 10 + index * 2,
          repeat: -1,
          ease: 'linear'
        });
      }
    });

    // Features Section Scroll Animation
    gsap.from(featuresSectionRef.current.querySelector('h2'), {
      scrollTrigger: {
        trigger: featuresSectionRef.current,
        start: 'top 80%',
        toggleActions: 'play none none reverse'
      },
      y: 50,
      opacity: 0,
      duration: 0.8,
      ease: 'power2.out'
    });

    gsap.from(featuresSectionRef.current.querySelector('p'), {
      scrollTrigger: {
        trigger: featuresSectionRef.current,
        start: 'top 80%',
        toggleActions: 'play none none reverse'
      },
      y: 30,
      opacity: 0,
      duration: 0.8,
      delay: 0.2,
      ease: 'power2.out'
    });

    // Feature Cards Stagger Animation
    featureCardsRef.current.forEach((card, index) => {
      if (card) {
        gsap.from(card, {
          scrollTrigger: {
            trigger: card,
            start: 'top 85%',
            toggleActions: 'play none none reverse'
          },
          y: 80,
          opacity: 0,
          rotation: -10,
          duration: 0.7,
          delay: index * 0.1,
          ease: 'back.out(1.2)'
        });
      }
    });

    // Download Section Animation
    gsap.from(downloadSectionRef.current.querySelector('h2'), {
      scrollTrigger: {
        trigger: downloadSectionRef.current,
        start: 'top 75%',
        toggleActions: 'play none none reverse'
      },
      scale: 0.8,
      opacity: 0,
      duration: 0.8,
      ease: 'back.out(1.4)'
    });

    gsap.from(downloadSectionRef.current.querySelector('p'), {
      scrollTrigger: {
        trigger: downloadSectionRef.current,
        start: 'top 75%',
        toggleActions: 'play none none reverse'
      },
      y: 30,
      opacity: 0,
      duration: 0.6,
      delay: 0.3
    });

    // Stats Counter Animation
    downloadStatsRef.current.forEach((stat, index) => {
      if (stat) {
        gsap.from(stat, {
          scrollTrigger: {
            trigger: stat,
            start: 'top 85%',
            toggleActions: 'play none none reverse'
          },
          scale: 0,
          opacity: 0,
          duration: 0.6,
          delay: index * 0.15,
          ease: 'elastic.out(1, 0.5)'
        });
      }
    });

    gsap.from(downloadSectionRef.current.querySelector('button'), {
      scrollTrigger: {
        trigger: downloadSectionRef.current,
        start: 'top 70%',
        toggleActions: 'play none none reverse'
      },
      y: 50,
      opacity: 0,
      duration: 0.8,
      delay: 0.6,
      ease: 'power3.out'
    });

    // About Section Animation
    gsap.from(aboutContentRef.current, {
      scrollTrigger: {
        trigger: aboutSectionRef.current,
        start: 'top 75%',
        toggleActions: 'play none none reverse'
      },
      x: -100,
      opacity: 0,
      duration: 1,
      ease: 'power2.out'
    });

    gsap.from(aboutTerminalRef.current, {
      scrollTrigger: {
        trigger: aboutSectionRef.current,
        start: 'top 75%',
        toggleActions: 'play none none reverse'
      },
      x: 100,
      opacity: 0,
      duration: 1,
      ease: 'power2.out'
    });

    // Terminal Text Line by Line Animation
    const terminalLines = aboutTerminalRef.current.querySelectorAll('.terminal-line');
    gsap.from(terminalLines, {
      scrollTrigger: {
        trigger: aboutTerminalRef.current,
        start: 'top 80%',
        toggleActions: 'play none none reverse'
      },
      x: -20,
      opacity: 0,
      duration: 0.4,
      stagger: 0.2,
      ease: 'power2.out'
    });
    });
  ScrollTrigger.refresh();
  return () => ctx.revert(); // cleanup on component unmount

  }, []);

  const features = [
    {
      icon: '📁',
      title: 'Easy Project Setup',
      description: 'Download React projects directly to your selected folder with just a few clicks'
    },
    {
      icon: '🔍',
      title: 'Package Search',
      description: 'Search and discover npm packages with installation commands ready to use'
    },
    {
      icon: '⚡',
      title: 'Fast & Efficient',
      description: 'Streamlined workflow for React developers to boost productivity'
    },
    {
      icon: '🛠️',
      title: 'Developer Friendly',
      description: 'Built by developers, for developers with intuitive interface'
    }
  ];

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = "/ReactNestSetup.exe";
    link.download = "ReactNestSetup.exe";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white font-sans overflow-x-hidden">
      {/* Animated gradient orbs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -left-40 w-80 h-80 bg-gradient-to-br from-purple-600/20 to-pink-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute top-1/2 -right-40 w-96 h-96 bg-gradient-to-bl from-blue-600/20 to-purple-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute -bottom-40 left-1/4 w-72 h-72 bg-gradient-to-tr from-pink-500/20 to-purple-600/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
      </div>

      {/* Navigation */}
      <nav className="relative z-50 bg-black/40 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <span className="text-2xl">
                <LogoPlaceholder className="w-14 h-14" />
              </span>
              <span className="text-xl font-bold bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 bg-clip-text text-transparent">
                React Nest
              </span>
            </div>
            
            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-gray-300 hover:text-white transition-colors duration-300">Features</a>
              <a href="#download" className="text-gray-300 hover:text-white transition-colors duration-300">Download</a>
              <a href="#about" className="text-gray-300 hover:text-white transition-colors duration-300">About</a>
              <Link to="/contact" className="text-gray-300 hover:text-white transition-colors duration-300">Contact</Link>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden text-white"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} />
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden bg-black/90 backdrop-blur-xl border-t border-white/10">
            <div className="px-6 py-4 space-y-4">
              <a href="#features" className="block text-gray-300 hover:text-white transition-colors">Features</a>
              <a href="#download" className="block text-gray-300 hover:text-white transition-colors">Download</a>
              <a href="#about" className="block text-gray-300 hover:text-white transition-colors">About</a>
              <Link to="/contact" className="text-gray-300 hover:text-white transition-colors duration-300">Contact</Link>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section with Floating Elements */}
      <section className="relative min-h-screen flex items-center justify-center px-6 lg:px-8 z-20">
        {/* Floating animated shapes */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div ref={el => floatingElementsRef.current[0] = el} className="absolute top-20 left-10 w-20 h-20 border-2 border-purple-500/30 rounded-lg"></div>
          <div ref={el => floatingElementsRef.current[1] = el} className="absolute top-40 right-20 w-16 h-16 border-2 border-blue-500/30 rounded-full"></div>
          <div ref={el => floatingElementsRef.current[2] = el} className="absolute bottom-40 left-20 w-24 h-24 border-2 border-pink-500/30 rounded-lg"></div>
          <div ref={el => floatingElementsRef.current[3] = el} className="absolute bottom-20 right-40 w-12 h-12 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-full blur-xl"></div>
          <div ref={el => floatingElementsRef.current[4] = el} className="absolute top-60 left-1/4 w-32 h-32 border border-blue-500/20 rounded-full"></div>
          <div ref={el => floatingElementsRef.current[5] = el} className="absolute top-32 right-1/3 w-8 h-8 bg-purple-500/40 rounded-sm"></div>
        </div>

        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div className="text-center lg:text-left space-y-8">
            <div className="space-y-4">
              <h1 ref={heroTitleRef} className="text-5xl lg:text-7xl font-black leading-tight">
                The Ultimate
              </h1>
              <span 
                ref={heroSubtitleRef}
                className="block text-5xl lg:text-7xl font-black bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 bg-clip-text text-transparent"
                style={{ backgroundSize: '200% auto' }}
              >
                React Tool
              </span>
              <p ref={heroDescriptionRef} className="text-xl text-gray-400 max-w-xl leading-relaxed">
                Streamline your React development with project setup, 
                smart package discovery, and lightning-fast deployment tools.
              </p>
            </div>

            <div ref={heroButtonRef} className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <button 
                onClick={handleDownload}
                className="group px-8 py-4 bg-gradient-to-r from-blue-500 via-purple-600 to-pink-500 rounded-2xl font-semibold text-lg transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-purple-500/50"
              >
                <span className="flex items-center justify-center space-x-3">
                  <span>Download Free</span>
                  <span className="group-hover:translate-x-1 transition-transform">→</span>
                </span>
              </button>
            </div>
          </div>

          {/* App Preview */}
          <div className="flex justify-center lg:justify-end">
            <div ref={heroPreviewRef} className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500/30 via-purple-500/30 to-pink-500/30 rounded-3xl blur-xl"></div>
              <div className="relative bg-white/10 backdrop-blur-xl border border-white/30 rounded-3xl p-1 w-80 lg:w-96 shadow-2xl">
                <div className="bg-gradient-to-r from-blue-500 via-purple-600 to-pink-500 rounded-t-3xl p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex space-x-2">
                      <div className="w-3 h-3 bg-white/40 rounded-full"></div>
                      <div className="w-3 h-3 bg-white/40 rounded-full"></div>
                      <div className="w-3 h-3 bg-white/40 rounded-full"></div>
                    </div>
                    <span className="text-white font-medium">React Nest</span>
                  </div>
                </div>
                
                <div className="p-6 space-y-4">
                  <div className="flex space-x-2">
                    <input 
                      type="text" 
                      placeholder="Search packages..." 
                      className="flex-1 bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-purple-500 transition-colors backdrop-blur-sm"
                    />
                    <button className="px-4 py-3 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl text-white hover:scale-105 transition-transform">
                      🔍
                    </button>
                  </div>
                  
                  <div className="space-y-3">
                    {['react-router-dom', 'axios', 'framer-motion'].map((pkg) => (
                      <div 
                        key={pkg} 
                        className="bg-white/10 rounded-xl p-4 border border-white/20 hover:bg-white/15 transition-all duration-300 backdrop-blur-sm"
                      >
                        <div className="flex justify-between items-center">
                          <span className="text-white font-medium">{pkg}</span>
                          <code className="text-xs bg-purple-500/30 text-purple-200 px-2 py-1 rounded border border-purple-400/20">
                            npm i {pkg}
                          </code>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section ref={featuresSectionRef} id="features" className="relative py-20 px-6 lg:px-8 z-20 bg-black/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold">
              Why Choose 
              <span className="bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 bg-clip-text text-transparent"> React Nest</span>?
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Experience the future of React development with cutting-edge features
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                ref={el => featureCardsRef.current[index] = el}
                className="group p-8 bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl hover:bg-white/10 transition-all duration-500 hover:scale-105 hover:border-purple-500/50 hover:shadow-2xl hover:shadow-purple-500/20"
              >
                <div className="text-center space-y-4">
                  <div className="text-5xl group-hover:scale-110 transition-transform duration-300">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-bold text-white">{feature.title}</h3>
                  <p className="text-gray-400 leading-relaxed">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Download Section */}
      <section ref={downloadSectionRef} id="download" className="relative py-20 px-6 lg:px-8 z-20 bg-black/30 backdrop-blur-sm">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 via-purple-600/10 to-pink-500/10"></div>
        <div className="relative max-w-5xl mx-auto text-center space-y-8">
          <h2 className="text-4xl lg:text-5xl font-bold">
            Ready to <span className="bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 bg-clip-text text-transparent">Transform</span> Your Workflow?
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Join thousands of developers who've already revolutionized their React development process
          </p>

          {/* Stats */}
          <div className="flex justify-center space-x-12 lg:space-x-20 my-12">
            {[
              { number: '10K+', label: 'Downloads' },
              { number: '4.9★', label: 'Rating' },
            ].map((stat, index) => (
              <div key={index} ref={el => downloadStatsRef.current[index] = el} className="text-center">
                <div className="text-3xl lg:text-4xl font-black bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 bg-clip-text text-transparent">
                  {stat.number}
                </div>
                <div className="text-gray-400 mt-2">{stat.label}</div>
              </div>
            ))}
          </div>

          <button 
            onClick={handleDownload}
            className="group relative px-12 py-6 bg-gradient-to-r from-blue-500 via-purple-600 to-pink-500 rounded-3xl font-bold text-xl transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-purple-500/50"
          >
            <div className="flex flex-col items-center space-y-2">
              <span className="flex items-center space-x-3">
                <span>Download React Nest</span>
                <span className="group-hover:translate-x-1 transition-transform">↓</span>
              </span>
              <span className="text-sm opacity-80">Windows • Free • 71.5MB</span>
            </div>
          </button>
        </div>
      </section>

      {/* About Section */}
      <section ref={aboutSectionRef} id="about" className="relative py-20 px-6 lg:px-8 z-20 bg-black/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div ref={aboutContentRef} className="space-y-6">
            <h2 className="text-4xl lg:text-5xl font-bold">
              Built for 
              <span className="bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 bg-clip-text text-transparent"> Modern React</span>
            </h2>
            <p className="text-xl text-gray-400 leading-relaxed">
              React Nest eliminates the friction in React development. From project initialization 
              to package management, everything is designed for maximum efficiency.
            </p>
            
            <div className="space-y-4">
              {[
                'One-click project setup',
                'Real-time dependency management',
                'Zero-config deployment'
              ].map((feature, index) => (
                <div key={index} className="flex items-center space-x-4">
                  <div className="w-6 h-6 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-sm">✓</span>
                  </div>
                  <span className="text-gray-300">{feature}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Terminal Preview */}
          <div ref={aboutTerminalRef} className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-2xl blur-xl"></div>
            <div className="relative bg-gray-900/90 backdrop-blur-sm border border-white/20 rounded-2xl overflow-hidden">
              <div className="bg-gray-800 px-6 py-4 flex items-center space-x-3">
                <div className="flex space-x-2">
                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                  <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                </div>
                <span className="text-gray-300 text-sm">Terminal</span>
              </div>
              <div className="p-6 font-mono text-sm space-y-2">
                <div className="terminal-line text-gray-400">$ react-nest create my-app</div>
                <div className="terminal-line text-purple-400">✨ Creating React project...</div>
                <div className="terminal-line text-gray-400">$ cd my-app</div>
                <div className="terminal-line text-gray-400">$ react-nest add router axios</div>
                <div className="terminal-line text-purple-400">📦 Installing packages...</div>
                <div className="terminal-line text-green-400">✅ Project ready in 30 seconds!</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative border-t border-white/10 py-12 px-6 lg:px-8 z-20 bg-black/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col justify-center items-center space-y-6">
            <div className="flex items-center space-x-3">
              <span className="text-2xl">
                <LogoPlaceholder className="w-14 h-14" />
              </span>
              <span className="text-xl font-bold bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 bg-clip-text text-transparent">
                React Nest
              </span>
            </div>
          </div>
        
          <div className="mt-8 pt-8 border-t border-white/10 text-center text-gray-400">
            <p>&copy; 2025 React Nest. Made with ❤️ for React developers.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;